import React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '../../ui/card';
import { cn } from '../../../lib/utils';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import {
  bucketLabel, formatMetric, formatTick, managerRows, OVERRIDE_SERIES, playerName, share, STATUS_SERIES, timeBuckets, totalLabel,
} from './statsView';

const AXIS_COLOR = '#7a8499';
const GRID_COLOR = 'rgba(255,255,255,0.06)';
const CURSOR_FILL = 'rgba(255,255,255,0.04)';
// a 2px surface gap between stacked segments
const SEGMENT_GAP = { stroke: '#15171f', strokeWidth: 2 }; // the card's surface-1
const MANAGER_ROW_HEIGHT = 34;

// StatTile: one headline number with a line under it.
export function StatTile({ label, value, sub }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-medium text-ink-mute">{label}</div>
      <div className="mt-1 font-display text-2xl font-black text-ink">{value}</div>
      {sub && <div className="mt-0.5 text-xs font-medium text-ink-dim">{sub}</div>}
    </Card>
  );
}

function Legend({ series }) {
  return (
    <div className="flex flex-wrap gap-3 text-xs font-medium text-ink-dim">
      {series.map((s) => (
        <span key={s.key} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

// SeriesTooltip: the hovered bar's segments and their total, in text colors.
function SeriesTooltip({ active, payload, label, series, metric, title }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-md border border-white/10 bg-surface-1 px-3 py-2 text-xs shadow-lg">
      <p className="mb-1 font-bold text-ink">{title ? title(row, label) : label}</p>
      {series.map((s) => (
        <p key={s.key} className="flex items-center justify-between gap-4 font-medium text-ink-dim">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: s.color }} />{s.label}
          </span>
          <span className="font-bold text-ink">{formatMetric(metric, row[`${s.key}_${metric}`])}</span>
        </p>
      ))}
      <p className="mt-1 flex justify-between gap-4 border-t border-white/10 pt-1 font-medium text-ink-dim">
        <span>Total</span><span className="font-bold text-ink">{formatMetric(metric, row[metric])}</span>
      </p>
    </div>
  );
}

function ChartCard({ title, sub, series, children }) {
  return (
    <Card className="space-y-3 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-bold text-ink">{title}</h2>
          {sub && <p className="text-xs font-medium text-ink-mute">{sub}</p>}
        </div>
        {series.length > 1 && <Legend series={series} />}
      </div>
      {children}
    </Card>
  );
}

// stackedBars: one Bar per series, stacked, the top one with rounded ends.
const stackedBars = (series, metric, radius) => series.map((s, i) => (
  <Bar
    key={s.key} dataKey={`${s.key}_${metric}`} name={s.label} stackId="a" fill={s.color} {...SEGMENT_GAP}
    radius={i === series.length - 1 ? radius : 0} isAnimationActive={false}
  />
));

// TimeChart: comps per day (or week), stacked by status; only the statuses that occur are drawn.
export function TimeChart({ days, metric, status, from, to }) {
  const { weekly, rows } = timeBuckets(days, { from, to });
  const series = STATUS_SERIES.filter((s) => rows.some((r) => r[`${s.key}_count`] > 0));
  const data = rows.map((r) => ({ ...r, label: bucketLabel(r.start, weekly) }));
  return (
    <ChartCard title={`${totalLabel(status)} per ${weekly ? 'week' : 'day'}`} sub="By the UTC day the comp was added" series={series}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={GRID_COLOR} vertical={false} />
          <XAxis dataKey="label" stroke={AXIS_COLOR} fontSize={11} tickLine={false} minTickGap={16} />
          <YAxis stroke={AXIS_COLOR} fontSize={11} width={64} tickLine={false} axisLine={false}
            allowDecimals={false} tickFormatter={(v) => formatTick(metric, v)} />
          <Tooltip cursor={{ fill: CURSOR_FILL }} content={<SeriesTooltip series={series} metric={metric} />} />
          {stackedBars(series, metric, [4, 4, 0, 0])}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ManagerChart: each manager's comps, regular and overrides stacked, most USD first.
export function ManagerChart({ managers, metric }) {
  const rows = managerRows(managers);
  return (
    <ChartCard title="By VIP manager" sub="Override: issued although a check failed" series={OVERRIDE_SERIES}>
      <ResponsiveContainer width="100%" height={rows.length * MANAGER_ROW_HEIGHT + 32}>
        <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={GRID_COLOR} horizontal={false} />
          <XAxis type="number" stroke={AXIS_COLOR} fontSize={11} tickLine={false} allowDecimals={false}
            tickFormatter={(v) => formatTick(metric, v)} />
          <YAxis type="category" dataKey="manager" stroke={AXIS_COLOR} fontSize={11} width={190} tickLine={false} axisLine={false} />
          <Tooltip cursor={{ fill: CURSOR_FILL }} content={<SeriesTooltip series={OVERRIDE_SERIES} metric={metric} />} />
          {stackedBars(OVERRIDE_SERIES, metric, [0, 4, 4, 0])}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// MetricBar: the value with a bar against the list's largest.
function MetricBar({ metric, value, max }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 rounded-sm bg-surface-2">
        <div className="h-2 rounded-sm bg-brand-blue" style={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }} />
      </div>
      <span className="w-24 text-right text-sm font-bold text-ink">{formatMetric(metric, value)}</span>
    </div>
  );
}

const TOP_PLAYERS_SUB = { usd: 'By USD received', count: 'By number of comps' };

// TopPlayers: the players who received the most USD, or the most comps (metric); the metric's
// column carries the bar and the share. A name opens the player's check.
export function TopPlayers({ players, totals, metric, onOpenPlayer }) {
  const max = Math.max(...players.map((p) => p[metric]), 0);
  const cell = (key, p) => (key === metric
    ? <MetricBar metric={key} value={p[key]} max={max} />
    : <span className="text-sm font-medium text-ink-dim">{formatMetric(key, p[key])}</span>);
  return (
    <ChartCard title={`Top ${players.length} players`} sub={TOP_PLAYERS_SUB[metric]} series={[]}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {['#', 'Player', 'Comps', 'Overrides', 'USD', 'Share'].map((h) => (
                <TableHead key={h} className="h-9 px-3 text-xs">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {players.map((p, i) => (
              <TableRow key={p.user_id}>
                <TableCell className="px-3 py-2 text-xs font-bold text-ink-mute">{i + 1}</TableCell>
                <TableCell className="px-3 py-2">
                  <button type="button" className="text-left" onClick={() => onOpenPlayer(String(p.user_id))} title="Check this player">
                    <div className="text-sm font-bold text-brand-blue hover:underline">{playerName(p)}</div>
                    <div className="text-xs font-medium text-ink-mute">ID {p.user_id}</div>
                  </button>
                </TableCell>
                <TableCell className={cn('px-3 py-2', metric === 'count' && 'w-56')}>{cell('count', p)}</TableCell>
                <TableCell className="px-3 py-2 text-sm font-medium text-ink-dim">{p.override_count || '—'}</TableCell>
                <TableCell className={cn('px-3 py-2', metric === 'usd' && 'w-56')}>{cell('usd', p)}</TableCell>
                <TableCell className="px-3 py-2 text-xs font-medium text-ink-dim">{share(p[metric], totals[metric])}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </ChartCard>
  );
}
